/**
 * Форматирование для интерфейса.
 *
 * Денежные суммы приходят с бэкенда уже готовой строкой: правило
 * форматирования принадлежит домену, и дублировать его здесь значит
 * однажды разойтись с ботом и чеками.
 */

const BYTE_UNITS = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'] as const;

export function formatBytes(value: number | null): string {
  if (value === null || value === 0) {
    return '∞';
  }
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }
  const rounded = unit === 0 ? String(size) : size.toFixed(1).replace('.', ',');
  return `${rounded} ${BYTE_UNITS[unit]}`;
}

/**
 * Короткая дата: 07.10.2026.
 *
 * Для тесных мест — карточек подписок и строк «действует до». Дата
 * словами там переносится на вторую строку и ломает выравнивание.
 */
export function formatShortDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

/** Индекс единицы измерения, в которой значение выглядит читаемо. */
function unitFor(bytes: number): number {
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return unit;
}

function scaleTo(bytes: number, unit: number): string {
  const value = bytes / 1024 ** unit;
  if (value >= 100 || Number.isInteger(value)) {
    return String(Math.round(value));
  }
  return value.toFixed(1).replace('.', ',').replace(/,0$/, '');
}

/**
 * Расход трафика вида «0/∞ ГБ» или «4,7/50 ГБ».
 *
 * Единица одна на обе части и выбирается по лимиту, а не по расходу:
 * иначе получается «0 Б / 50 ГБ», где числа по разные стороны дроби
 * несопоставимы на глаз. При безлимите единица не ниже гигабайта —
 * «0/∞ Б» выглядит ошибкой.
 */
export function formatTraffic(usedBytes: number, limitBytes: number | null): string {
  const GIGABYTE = 3;
  const unit =
    limitBytes === null ? Math.max(GIGABYTE, unitFor(usedBytes)) : unitFor(limitBytes);
  const used = scaleTo(usedBytes, unit);
  const limit = limitBytes === null ? '∞' : scaleTo(limitBytes, unit);
  return `${used}/${limit} ${BYTE_UNITS[unit]}`;
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
}

/**
 * Склонение слова «день» без самого числа.
 *
 * Нужно отдельно: на карточке-герое число набрано крупно, а слово рядом
 * мелким — склеенная строка там не годится.
 */
export function daysWord(count: number): string {
  const abs = Math.abs(count) % 100;
  const tail = abs % 10;
  if (abs > 10 && abs < 20) return 'дней';
  if (tail === 1) return 'день';
  if (tail >= 2 && tail <= 4) return 'дня';
  return 'дней';
}

/** Склонение: 1 день, 2 дня, 5 дней. */
export function formatDays(count: number): string {
  return `${count} ${daysWord(count)}`;
}

export function formatPeriod(code: string, durationDays: number | null): string {
  const named: Record<string, string> = {
    '1m': '1 месяц',
    '3m': '3 месяца',
    '6m': '6 месяцев',
    '1y': '1 год',
  };
  return named[code] ?? (durationDays ? formatDays(durationDays) : code);
}

const rubles = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** Сумма в копейках как «1 299 ₽» или «49,90 ₽»: копейки только если они есть. */
export function formatRub(minor: number): string {
  const value = minor / 100;
  const text = Number.isInteger(value)
    ? rubles.format(value)
    : new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  return `${text} ₽`;
}
